import React, { useState, useRef } from 'react';
import { useMsal, AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { Button } from './components/ui/button';
import { Upload, FileSpreadsheet, Download, Loader2, AlertCircle, User, LogIn } from "lucide-react";
import { CONFIG, getApiUrl } from './config';
import { getTokenMicro } from './msal_service';
import { loginRequest } from './auth_config';

// Componente de Login
function LoginComponent() {
  const { instance } = useMsal();

  const handleLogin = () => {
    instance.loginRedirect(loginRequest).catch((e) => {
      console.error('Erro no login:', e);
    });
  };

  return (
    <div className="min-h-screen bg-gradient-scala flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
        <div className="text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-gradient-scala-icon rounded-full flex items-center justify-center mx-auto mb-4">
              <FileSpreadsheet className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Backlog de NFs</h1>
            <p className="text-gray-600">Scala Data Centers</p>
          </div>
          
          <div className="mb-6">
            <p className="text-gray-700 mb-4">
              Sistema inteligente para análise e compilação de dados de Material e Serviços
            </p>
          </div>

          <Button
            onClick={handleLogin}
            className="w-full btn-gradient hover:bg-gradient-scala-light text-white font-semibold py-3 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center space-x-2"
          >
            <LogIn className="w-5 h-5" />
            <span>Entrar com Microsoft</span>
          </Button>

          <p className="text-xs text-gray-500 mt-4">
            Use sua conta corporativa para acessar o sistema
          </p>
        </div>
      </div>
    </div>
  );
}

// Componente Principal da Aplicação
function MainApp() {
  const { accounts, instance } = useMsal();
  const [materialFile, setMaterialFile] = useState(null);
  const [servicoFile, setServicoFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [compiledData, setCompiledData] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [error, setError] = useState(null);
  
  const materialInputRef = useRef(null);
  const servicoInputRef = useRef(null);

  // Obter informações do usuário logado
  const currentUser = accounts[0];

  const handleLogout = () => {
    instance.logoutRedirect();
  };

  const handleFileUpload = (file, type) => {
    if (file && file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      if (type === 'material') {
        setMaterialFile(file)
      } else {
        setServicoFile(file)
      }
      setError(null)
    } else {
      setError('Por favor, selecione apenas arquivos .xlsx')
    }
  }

  const processFiles = async () => {
    if (!materialFile || !servicoFile) {
      setError('Por favor, selecione ambos os arquivos antes de processar')
      return
    }

    setIsLoading(true)
    setError(null)
    
    try {
      // Obter token de autenticação
      const token = await getTokenMicro();
      
      const formData = new FormData()
      formData.append('material', materialFile)
      formData.append('servico', servicoFile)

      const apiUrl = getApiUrl(CONFIG.PROCESSAR_ENDPOINT);
      console.log('🔗 Enviando requisição para:', apiUrl);
      console.log('📁 Arquivos:', { 
        material: materialFile.name, 
        servico: servicoFile.name 
      });

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-User-Email': currentUser?.username || '',
        },
        body: formData,
      })

      console.log('📡 Response status:', response.status);
      console.log('📡 Response headers:', Object.fromEntries(response.headers));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Erro da API:', errorText);
        throw new Error(`Erro do servidor: ${response.status} ${response.statusText}. ${errorText}`)
      }

      const result = await response.json()
      console.log('✅ Resposta da API:', result);
      
      if (result.erro) {
        throw new Error(result.erro)
      }
      
      if (result.base64) {
        // Converter base64 para blob
        const binaryString = atob(result.base64)
        const bytes = new Uint8Array(binaryString.length)
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i)
        }
        
        const blob = new Blob([bytes], { 
          type: result.filetype || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        })
        const url = URL.createObjectURL(blob)
        
        setDownloadUrl(url)
        setCompiledData({ 
          success: true, 
          filename: result.filename,
          filetype: result.filetype 
        })
      } else {
        throw new Error('Resposta inválida do servidor')
      }
      
    } catch (error) {
      console.error('❌ Erro ao processar arquivos:', error)
      
      // Melhor tratamento de erro baseado no tipo
      let errorMessage = 'Erro ao processar os arquivos. Verifique sua conexão e tente novamente.';
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = 'Erro de conexão: Não foi possível conectar com o servidor. Verifique se o backend está rodando.';
      } else if (error.message.includes('401')) {
        errorMessage = 'Erro de autenticação: Token inválido ou expirado. Tente fazer login novamente.';
      } else if (error.message.includes('500')) {
        errorMessage = 'Erro interno do servidor: Verifique os logs do backend.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const downloadCompiledFile = () => {
    if (downloadUrl) {
      const link = document.createElement('a')
      link.href = downloadUrl
      const filename = compiledData?.filename || `relatorio_nfe_${new Date().toISOString().slice(0, 19).replace(/[:-]/g, '')}.xlsx`
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  const resetFiles = () => {
    setMaterialFile(null)
    setServicoFile(null)
    setCompiledData(null)
    setDownloadUrl(null)
    setError(null)
    if (materialInputRef.current) materialInputRef.current.value = ''
    if (servicoInputRef.current) servicoInputRef.current.value = ''
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#4682B4] to-[#2F4F4F] text-white py-4">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center">
            <div className="flex-1">
              <h1 className="header-title mb-1">Backlog de NFs</h1>
              <p className="header-subtitle text-blue-100">Sistema inteligente para análise e compilação de dados de Material e Serviços</p>
            </div>
            {currentUser && (
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <p className="text-sm font-medium">{currentUser.name || currentUser.username}</p>
                  <p className="text-xs text-blue-100">{currentUser.username}</p>
                </div>
                <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5" />
                </div>
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  className="text-black border-white hover:bg-white hover:text-[#4682B4] text-sm px-3 py-1"
                >
                  Sair
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-4">
      
        
        {/* Cards de Upload */}
        <div className="grid md:grid-cols-2 gap-4 section-spacing max-w-4xl mx-auto">
          {/* Card Material */}
          <div className="bg-white rounded-xl shadow-lg border-2 border-gray-100 hover:border-[#4682B4] transition-all duration-300 hover:shadow-xl">
            <div className="card-padding">
              <div className="flex items-center justify-center w-10 h-10 bg-[#4682B4] rounded-full mb-1 mx-auto">
                <FileSpreadsheet className="upload-icon text-white" />
              </div>
              <h3 className="title-compact font-semibold text-[#2F4F4F] text-center mb-2">Arquivo de Material</h3>
              <div className="space-y-4">
                <input
                  ref={materialInputRef}
                  type="file"
                  accept=".xlsx"
                  onChange={(e) => handleFileUpload(e.target.files[0], 'material')}
                  className="hidden"
                  id="material-upload"
                />
                <label
                  htmlFor="material-upload"
                  className="upload-area flex items-center justify-center w-full border-2 border-dashed border-[#4682B4] rounded-lg cursor-pointer hover:bg-blue-50 transition-colors duration-200"
                >
                  <div className="text-center">
                    <Upload className="upload-icon text-[#4682B4] mx-auto mb-2" />
                    <p className="text-[#4682B4] font-medium text-compact">Clique para selecionar arquivo</p>
                    <p className="text-gray-500 text-xs mt-1">Apenas arquivos .xlsx</p>
                  </div>
                </label>
                {materialFile && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-2">
                    <p className="text-green-700 text-xs font-medium">✓ {materialFile.name}</p>
                    <p className="text-green-600 text-xs mt-1">
                      {(materialFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Card Serviço */}
          <div className="bg-white rounded-xl shadow-lg border-2 border-gray-100 hover:border-[#2F4F4F] transition-all duration-300 hover:shadow-xl">
            <div className="card-padding">
              <div className="flex items-center justify-center w-10 h-10 bg-[#2F4F4F] rounded-full mb-3 mx-auto">
                <FileSpreadsheet className="upload-icon text-white" />
              </div>
              <h3 className="title-compact font-semibold text-[#2F4F4F] text-center mb-2">Arquivo de Serviço</h3>
              <div className="space-y-4">
                <input
                  ref={servicoInputRef}
                  type="file"
                  accept=".xlsx"
                  onChange={(e) => handleFileUpload(e.target.files[0], 'servico')}
                  className="hidden"
                  id="servico-upload"
                />
                <label
                  htmlFor="servico-upload"
                  className="upload-area flex items-center justify-center w-full border-2 border-dashed border-[#2F4F4F] rounded-lg cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                >
                  <div className="text-center">
                    <Upload className="upload-icon text-[#2F4F4F] mx-auto mb-2" />
                    <p className="text-[#2F4F4F] font-medium text-compact">Clique para selecionar arquivo</p>
                    <p className="text-gray-500 text-xs mt-1">Apenas arquivos .xlsx</p>
                  </div>
                </label>
                {servicoFile && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-2">
                    <p className="text-green-700 text-xs font-medium">✓ {servicoFile.name}</p>
                    <p className="text-green-600 text-xs mt-1">
                      {(servicoFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mensagem de Erro */}
        {error && (
          <div className="section-spacing bg-red-50 border border-red-200 rounded-lg card-padding max-w-xl mx-auto">
            <div className="flex items-center space-x-2">
              <AlertCircle className="status-icon text-red-500 flex-shrink-0" />
              <div>
                <h4 className="text-red-800 font-semibold text-compact">Erro no processamento</h4>
                <p className="text-red-700 text-xs mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Botões de Ação */}
        <div className="text-center space-y-4 my-8">
          {!isLoading && !compiledData && (
            <Button
              onClick={processFiles}
              disabled={!materialFile || !servicoFile}
              className="btn-primary bg-gradient-to-r from-[#4682B4] to-[#2F4F4F] hover:from-[#5a9bd4] hover:to-[#3a5f5f] text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed my-6 px-8 py-3"
            >
              Enviar para Processamento
            </Button>
          )}

          {isLoading && (
            <div className="flex flex-col items-center space-y-3">
              <div className="flex items-center space-x-2">
                <Loader2 className="status-icon text-[#4682B4] animate-spin" />
                <span className="text-compact font-medium text-[#2F4F4F]">Processando no servidor...</span>
              </div>
              <div className="w-48 bg-gray-200 rounded-full h-1.5">
                <div className="bg-gradient-to-r from-[#4682B4] to-[#2F4F4F] h-1.5 rounded-full animate-pulse"></div>
              </div>
              <p className="text-gray-600 text-xs">Aguarde o processamento no backend</p>
            </div>
          )}

          {compiledData && downloadUrl && (
            <div className="space-y-3">
              <div className="bg-green-50 border border-green-200 rounded-lg card-padding max-w-sm mx-auto">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-sm">✓</span>
                  </div>
                  <span className="text-green-700 font-semibold text-compact">Processamento Concluído!</span>
                </div>
                <p className="text-green-600 text-center text-xs">Arquivos processados pelo servidor com sucesso</p>
                <p className="text-green-600 text-center text-xs mt-1">
                  Relatório consolidado pronto para download
                </p>
              </div>
              
              <div className="flex justify-center space-x-3">
                <Button
                  onClick={downloadCompiledFile}
                  className="btn-primary bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center space-x-1"
                >
                  <Download className="status-icon" />
                  <span>Baixar Relatório NFe</span>
                </Button>
                
                <Button
                  onClick={resetFiles}
                  variant="outline"
                  className="btn-secondary border-[#4682B4] text-[#4682B4] hover:bg-[#4682B4] hover:text-white rounded-lg transition-all duration-300"
                >
                  Processar Novos Arquivos
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Instruções */}
        <div className="mt-6 bg-gray-50 rounded-xl card-padding">
          <h3 className="title-compact font-semibold text-[#2F4F4F] text-center mb-4">Como usar</h3>
          <div className="instructions-grid grid md:grid-cols-3">
            <div className="instruction-item text-center">
              <div className="instruction-number bg-[#4682B4] text-white rounded-full flex items-center justify-center mx-auto mb-2 font-bold">1</div>
              <h4 className="font-semibold text-[#2F4F4F] mb-1 text-compact">Upload dos Arquivos</h4>
              <p className="text-gray-600 text-xs">Selecione os arquivos .xlsx de Material e Serviço</p>
            </div>
            <div className="instruction-item text-center">
              <div className="instruction-number bg-[#4682B4] text-white rounded-full flex items-center justify-center mx-auto mb-2 font-bold">2</div>
              <h4 className="font-semibold text-[#2F4F4F] mb-1 text-compact">Processamento no Servidor</h4>
              <p className="text-gray-600 text-xs">Clique em "Enviar para Processamento" e aguarde o backend processar</p>
            </div>
            <div className="instruction-item text-center">
              <div className="instruction-number bg-[#4682B4] text-white rounded-full flex items-center justify-center mx-auto mb-2 font-bold">3</div>
              <h4 className="font-semibold text-[#2F4F4F] mb-1 text-compact">Download</h4>
              <p className="text-gray-600 text-xs">Baixe o relatório NFe gerado pelo servidor</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <>
      <UnauthenticatedTemplate>
        <LoginComponent />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <MainApp />
      </AuthenticatedTemplate>
    </>
  );
}

export default App;
